import { roundness } from "@provider/dripsy";
import { Text, TextInput, View } from "dripsy";
import * as React from "react";

import AmountButton from "./amount-button";
import DeleteIcon from "./icons/delete-icon";
import MinusIcon from "./icons/minus-icon";
import PlusIcon from "./icons/plus-icon";
import { notSelectableOnWebStyle } from "@utils";

const hitSlopLeft = { top: 10, bottom: 10, right: 0, left: 10 };
const hitSlopRight = { top: 10, bottom: 10, right: 30, left: 10 };
function Amount({
  value,
  onChange,
  shouldStretch = false,
  disabled = false,
  discount = false,
  fallback,
  possible,
  alwaysShowPossible = false,
  ref,
}: {
  value: number;
  onChange: (v: number) => void;
  shouldStretch?: boolean;
  disabled?: boolean;
  discount?: boolean;
  fallback?: React.ReactNode;
  possible?: number;
  alwaysShowPossible?: boolean;
  ref?: React.Ref<any>;
}) {
  const [focused, setFocused] = React.useState(false);
  const onRemove = () => onChange?.(value - 1);
  const onAdd = () => {
    // No longer check maximum here - let parent handle it
    onChange?.(value + 1);
  };

  const showPossible = possible !== undefined && possible !== null && (alwaysShowPossible || value !== possible);
  return (
    <View
      sx={{
        flexDirection: "row",
        alignItems: "center",
        minHeight: 40,
      }}
    >
      {value > 0 || focused ? (
        <>
          {disabled ? null : (
            <AmountButton testID="amount-remove" accessibilityLabel="Verminderen" hitslop={hitSlopLeft} onPress={onRemove} backgroundColor="$secondaryBackground" bordered>
              {value === 1 ? (
                <DeleteIcon width={15} height={16} color={discount ? "$orange" : "$primary"} />
              ) : (
                <MinusIcon size={32} color={discount ? "$orange" : "$primary"} />
              )}
            </AmountButton>
          )}
          <View
            sx={{
              mx: 8,
              minWidth: showPossible ? 54 : 36,
              flex: shouldStretch ? 1 : 0,
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <TextInput
              ref={ref}
              value={`${value === 0 ? "" : value}`}
              onChangeText={(newValue) => {
                const val = parseToInt(newValue);
                if (val < 0) {
                  onChange(0);
                  return;
                }
                // No longer limit input - let parent handle validation
                onChange(val);
              }}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              keyboardType="number-pad"
              sx={{
                fontSize: 14,
                fontWeight: "bold",
                borderRadius: roundness,
                borderWidth: 2,
                minWidth: 32,
                maxWidth: shouldStretch ? undefined : 32,
                width: shouldStretch ? undefined : 32,
                height: 36,
                textAlign: "center",
                textAlignVertical: "center",
                padding: 0,
                borderColor: discount
                  ? focused
                    ? "$orange"
                    : "$orangeLight"
                  : focused
                  ? "$primary"
                  : "$lightGreenOpacity",
                backgroundColor: "$background",
                color: discount ? "$orangePressed" : "$primary",
                outlineStyle: "none",
                ...notSelectableOnWebStyle,
              }}
              editable={!disabled}
            />
            {showPossible ? (
              <>
                <Text
                  selectable={false}
                  sx={{ fontWeight: "600", opacity: 0.6, marginLeft: 4, marginRight: 2, fontSize: 11 }}
                >
                  /
                </Text>
                <Text selectable={false} sx={{ fontWeight: "600", opacity: 0.6, fontSize: 11 }}>
                  {possible}
                </Text>
              </>
            ) : null}
          </View>
        </>
      ) : null}

      {disabled ? null : !value && !focused && fallback ? (
        fallback
      ) : (
        <AmountButton
          testID="amount-add"
          accessibilityLabel="Toevoegen aan mand"
          hitslop={hitSlopRight}
          onPress={onAdd}
          backgroundColor={discount ? "$orange" : "$primary"}
        >
          <PlusIcon size={28} color="$background" />
        </AmountButton>
      )}
    </View>
  );
}

export default Amount;

function parseToInt(s: string): number {
  const int = parseInt(s, 10);
  if (isNaN(int)) {
    return -1;
  }

  return int;
}
